import { useState } from 'react';
import { ArrowDown, ArrowUp, Copy, Eye, EyeOff, MonitorPlay, RotateCcw, Trash2 } from 'lucide-react';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { PhotosEmplacements } from './PhotosEmplacements';
import { ApercuDiapo } from './ApercuDiapo';

const LISTES = ['puces', 'chiffres', 'col1', 'col2', 'col3'];
const pareil = (a, b) => JSON.stringify(a) === JSON.stringify(b);

// Une diapositive : champs modifiables selon son type, photos et retour au texte d'origine
export const DiapoCarte = ({ n, d, api, valeur, defaut, refs, cadrages, edition, onChange, onPhotos, onSlot, onRecadrer, onDeplacer, premier, dernier,
  onDupliquer, onSupprimer }) => {
  const [apercu, setApercu] = useState(false);
  const modifie = ['surtitre', 'titre', 'intro', 'notes', ...LISTES].some((k) => !pareil(valeur[k] || [], defaut[k] || []));
  const maj = (k, v) => onChange({ ...valeur, [k]: LISTES.includes(k) ? v.split('\n') : v });
  const masquee = !!valeur.masquee;
  return (
    <div className={`rounded-xl border p-4 transition-colors ${masquee ? 'border-dashed border-slate-300 bg-slate-50 [&>*:not(:first-child)]:opacity-50' : 'border-slate-200 bg-white'}`}
      data-testid={`presentation-diapo-${d.cle}`}>
      <div className="mb-3 flex items-center gap-3">
        <span className="text-xl font-black text-[#8DC63F]">{String(n).padStart(2, '0')}</span>
        <h3 className="flex-1 font-semibold text-[#0F172A]">{d.libelle}{masquee && <span className="ml-2 text-xs font-normal text-slate-500">(masquée)</span>}</h3>
        {edition && (
          <span className="inline-flex">
            <button type="button" onClick={() => onDeplacer(-1)} disabled={premier} aria-label="Monter" data-testid={`presentation-monter-${d.cle}`}
              className="rounded p-1 text-slate-500 transition-colors hover:bg-slate-100 hover:text-[#0F172A] disabled:opacity-30"><ArrowUp className="h-4 w-4" /></button>
            <button type="button" onClick={() => onDeplacer(1)} disabled={dernier} aria-label="Descendre" data-testid={`presentation-descendre-${d.cle}`}
              className="rounded p-1 text-slate-500 transition-colors hover:bg-slate-100 hover:text-[#0F172A] disabled:opacity-30"><ArrowDown className="h-4 w-4" /></button>
          </span>
        )}
        <button type="button" onClick={() => setApercu(!apercu)} data-testid={`presentation-voir-apercu-${d.cle}`}
          className={`inline-flex items-center gap-1 text-xs transition-colors ${apercu ? 'font-semibold text-[#0F172A]' : 'text-slate-500 hover:text-[#0F172A]'}`}>
          <MonitorPlay className="h-3.5 w-3.5" /> Aperçu</button>
        {edition && (
          <button type="button" onClick={onDupliquer} data-testid={`presentation-dupliquer-${d.cle}`}
            className="inline-flex items-center gap-1 text-xs text-slate-500 transition-colors hover:text-[#0F172A]"><Copy className="h-3.5 w-3.5" /> Dupliquer</button>
        )}
        {edition && d.copie && (
          <button type="button" onClick={onSupprimer} data-testid={`presentation-supprimer-${d.cle}`}
            className="inline-flex items-center gap-1 text-xs text-slate-500 transition-colors hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /> Supprimer</button>
        )}
        {edition && (
          <button type="button" onClick={() => onChange({ ...valeur, masquee: !masquee })} data-testid={`presentation-masquer-${d.cle}`}
            className="inline-flex items-center gap-1 text-xs text-slate-500 transition-colors hover:text-[#0F172A]">
            {masquee ? <><Eye className="h-3.5 w-3.5" /> Afficher</> : <><EyeOff className="h-3.5 w-3.5" /> Masquer</>}
          </button>
        )}
        {edition && modifie && (
          <button type="button" onClick={() => onChange({ ...defaut, masquee })} data-testid={`presentation-retablir-${d.cle}`}
            className="inline-flex items-center gap-1 text-xs text-slate-500 transition-colors hover:text-[#0F172A]">
            <RotateCcw className="h-3.5 w-3.5" /> Remettre le texte d'origine
          </button>
        )}
      </div>
      {apercu && <ApercuDiapo api={api} cle={d.cle} numero={n} valeur={valeur} refs={refs} cadrages={cadrages} className="mb-3 max-w-2xl" />}
      {d.nb_photos > 0 && (
        <PhotosEmplacements d={d} refs={refs} cadrages={cadrages} edition={edition} onChanger={onSlot} onRecadrer={onRecadrer} onToutes={onPhotos} />
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        {d.champs.map((c) => {
          const liste = LISTES.includes(c.cle);
          const v = liste ? (valeur[c.cle] || []).join('\n') : valeur[c.cle] || '';
          return (
            <label key={c.cle} className={`text-xs font-medium text-slate-600 ${liste ? 'sm:col-span-2' : ''}`}>
              {c.libelle}{c.aide && <span className="ml-1 font-normal text-slate-400">({c.aide})</span>}
              {liste
                ? <Textarea value={v} readOnly={!edition} onChange={(e) => maj(c.cle, e.target.value)} rows={Math.min(8, v.split('\n').length + 1)}
                    className="mt-1 text-sm" data-testid={`presentation-${d.cle}-${c.cle}`} />
                : <Input value={v} readOnly={!edition} onChange={(e) => maj(c.cle, e.target.value)} className="mt-1 text-sm" data-testid={`presentation-${d.cle}-${c.cle}`} />}
            </label>
          );
        })}
        <label className="text-xs font-medium text-slate-600 sm:col-span-2">
          Notes pour l'orateur <span className="font-normal text-slate-400">(visibles seulement dans PowerPoint)</span>
          <Textarea value={valeur.notes || ''} readOnly={!edition} onChange={(e) => maj('notes', e.target.value)} rows={2}
            className="mt-1 text-sm" data-testid={`presentation-${d.cle}-notes`} />
        </label>
      </div>
    </div>
  );
};
